import { GLContext } from 'nanogl/types'
import Node from 'nanogl-gltf/lib/elements/Node'
import Gltf from 'nanogl-gltf/lib/Gltf'
import MeshRenderer from 'nanogl-gltf/lib/renderer/MeshRenderer'
import GltfTypes from 'nanogl-gltf/lib/types/GltfTypes'

import { RenderContext } from '@/webgl/core/RenderContext'
import FontAtlas from '@/webgl/engine/FontAtlas'
import GltfResource from '@/webgl/resources/GltfResource'
import { BonusType } from '@/webgl/runner/entities/BonusEntity'
import { CollectibleEntity } from '@/webgl/runner/entities/CollectibleEntity'
import { ShoppingMode } from '@/webgl/runner/entities/ShoppingEntity'
import GameWorld from '@/webgl/runner/GameWorld'
import ILoadable from '@/webgl/runner/interfaces/ILoadable'
import { IRenderable } from '@/webgl/runner/interfaces/IRenderable'
import TextRenderer from '@/webgl/runner/rendering/TextRenderer'

type RenderItem = {
  collectible: CollectibleEntity
  text: TextRenderer
  view: number
  altered: boolean
  mode: ShoppingMode
}

type LabelItem = {
  label: Node
  tag: MeshRenderer
}

export default class PriceTagRenderer implements ILoadable, IRenderable {
  node: Node
  gltf: Gltf
  font: FontAtlas

  priceCache: Map<number | string, TextRenderer> = new Map()

  _labelsDefault: LabelItem[]
  _labelsRed: LabelItem[]
  _altered: MeshRenderer[]

  _items: RenderItem[] = []

  constructor(
    readonly gl: GLContext,
    readonly world: GameWorld
  ) {
    this.node = new Node()
  }

  setFont(font: FontAtlas) {
    this.font = font
  }

  async load(abort: AbortSignal): Promise<void> {
    this.gltf = await new GltfResource('francesca_2/price_tag.glb', this.gl).load(abort)
    this.node.add(this.gltf.root)

    this._labelsDefault = [
      {
        label: this.gltf.getElementByName(GltfTypes.NODE, 'label_l'),
        tag: this.gltf.getElementByName(GltfTypes.NODE, 'tag_l').renderable,
      },
      {
        label: this.gltf.getElementByName(GltfTypes.NODE, 'label_r'),
        tag: this.gltf.getElementByName(GltfTypes.NODE, 'tag_r').renderable,
      },
    ]

    this._labelsRed = [
      {
        label: this.gltf.getElementByName(GltfTypes.NODE, 'label_l'),
        tag: this.gltf.getElementByName(GltfTypes.NODE, 'tag_red_l').renderable,
      },
      {
        label: this.gltf.getElementByName(GltfTypes.NODE, 'label_r'),
        tag: this.gltf.getElementByName(GltfTypes.NODE, 'tag_red_r').renderable,
      },
    ]

    this._altered = [
      this.gltf.getElementByName(GltfTypes.NODE, 'altered_l').renderable,
      this.gltf.getElementByName(GltfTypes.NODE, 'altered_r').renderable,
    ]
  }

  preRender(dt: number): void {}

  prepare(entities: CollectibleEntity[], mode: ShoppingMode = ShoppingMode.Shop) {
    this._items = []

    const coins = this.world.isBonusActive(BonusType.PriceCoins)

    for (const collectible of entities) {
      let value: string | number = collectible.data.prices[collectible.priceID]
      if (coins) {
        value = ''
        for (let i = 0; i < collectible.priceID; i++) {
          value += '$'
        }
      }
      let label = this.priceCache.get(value)

      if (!label) {
        label = new TextRenderer(this.gl)
        label.setStyle({
          fontSize: 0.15,
          center: true,
          color: 0xfff4e5,
        })
        label.setFont(this.font)
        label.setText('$' + value.toString())
        this.priceCache.set(value, label)
      }

      this._items.push({
        collectible: collectible,
        text: label,
        view: collectible.seed < 0.5 ? 0 : 1,
        altered: collectible.blur > 0,
        mode: mode,
      })
    }
  }

  render(context: RenderContext): void {
    for (const item of this._items) {
      this.node.setMatrix(item.collectible.node._wmatrix)
      this.node.updateWorldMatrix()

      if (item.altered) {
        this._altered[item.view].render(context.gl, context.camera, context.mask, context.pass, context.glConfig)
        continue
      }

      // const labels = item.mode == ShoppingMode.Balance ? this._labelsRed : this._labelsDefault;
      const labels = this._labelsDefault //item.mode == ShoppingMode.Balance ? this._labelsRed : this._labelsDefault;
      const variant = labels[1]

      variant.tag.render(context.gl, context.camera, context.mask, context.pass, context.glConfig)

      item.text.node._wmatrix.set(variant.label._wmatrix)
      item.text.render(context)
    }
  }
}
